/**
 * StepEducatorSubjects.js
 *
 * An onboarding step for educators to select the subjects they teach.
 * Uses tagNodeUtils to manage the facade state of selected subject tags.
 */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import TagTypes from '@/constants/TagTypes';
import useTags from '@/hooks/useTags';
import FormTemplate from '@/dialogs_common/SetupTemplate';
import { useOnboarding } from '@/pages/setup/account/OnboardingContext';
import {PayloadStates} from '@lore/utils';
import Loader from '@/components/Loader';
import TagCheckboxCards from '@/pages/setup/_common/TagCheckboxCards';
import TagNodeUpdateDialog from '@/pages/setup/_common/TagNodeUpdateDialog';
import Overlay from '@/forms/_common/Overlay';
import RequestError from '@/forms/_common/RequestError';
import { toggleFacadeTagNode, saveFacadeTagNodes, updateFacadeTagNodeDescription } from '@/pages/setup/account/tagNodeUtils';

StepEducatorSubjects.propTypes = {
  user: PropTypes.object,
};

/**
 * The educator subjects selection step in the onboarding wizard.
 *
 * @param {object} _props - Component props.
 * @returns {JSX.Element} The rendered educator subjects step.
 */
export default function StepEducatorSubjects(_props) {
  const { updateData, registerOnBeforeNext, setNextDisabled, data, tagNodeCreate, tagNodeUpdate, tagNodeDestroy, refetchUser, saving, error, request, loading } = useOnboarding();

  const [userSubjects, setUserSubjects] = useState(data.userSubjects ?? []);
  const [softDeletes, setSoftDeletes] = useState(data.userSubjectsSoftDeletes ?? [])
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    if (data.userSubjects) {
      setUserSubjects(data.userSubjects);
    }
  }, [data.userSubjects]);

  useEffect(() => {
    if (data.userSubjectsSoftDeletes) {
      setSoftDeletes(data.userSubjectsSoftDeletes);
    }
  }, [data.userSubjectsSoftDeletes]);
  const [editingDialog, setEditingDialog] = useState(null); // { subject, userSubject }

  // Fetch available tags of type 'Subject'
  const subjects = useTags(TagTypes.Subject);

  useEffect(() => {
    setNextDisabled(userSubjects.length === 0);
  }, [userSubjects, setNextDisabled]);

  useEffect(() => {
    registerOnBeforeNext(async () => {
      setIsProcessing(true);
      try {
        const updatedSoftDeletes = await saveFacadeTagNodes({
          items: userSubjects,
          softDeletes,
          userId: data.id,
          tagNodeCreate,
          tagNodeUpdate,
          tagNodeDestroy,
        });
        await refetchUser();
        updateData({ userSubjectsSoftDeletes: updatedSoftDeletes });
        setIsProcessing(false);
        return true;
      } catch (e) {
        setIsProcessing(false);
        console.error('Failed to save subjects', e);
        return false;
      }
    });
  }, [userSubjects, softDeletes, updateData, registerOnBeforeNext, tagNodeCreate, tagNodeUpdate, tagNodeDestroy, refetchUser, data.id, setIsProcessing]);

  /**
   * Toggles the selection of a subject tag.
   *
   * @param {object} subject - The subject tag being toggled.
   */
  function handleChange(subject) {
    const { items, softDeletes: newSoftDeletes } = toggleFacadeTagNode(userSubjects, softDeletes, subject);
    setUserSubjects(items);
    setSoftDeletes(newSoftDeletes);
  }
  /**
   * Opens the dialog to edit details for a specific subject.
   *
   * @param {object} subject - The subject tag.
   * @param {object} userSubject - The user's subject facade node.
   */
  function handleDetailsChange(subject, userSubject) {
    setEditingDialog({ subject, userSubject });
  }

  /**
   * Saves the updated description for a subject.
   *
   * @param {string} description - The new description.
   */
  function handleDescriptionSave(description) {
    setUserSubjects((prev) => updateFacadeTagNodeDescription(prev, editingDialog.userSubject._tag.id, description));
    setEditingDialog(null);
  }

  if (loading || subjects.state === PayloadStates.FETCHING) return (<Loader />)

  return (
    <Overlay isVisible={saving || isProcessing}>
      <div className="mt-6">
        {editingDialog && (
          <TagNodeUpdateDialog
            tag={editingDialog.subject}
            tagNode={editingDialog.userSubject._tagNode}
            onSave={handleDescriptionSave}
            onCancel={() => setEditingDialog(null)}
          />
        )}
        {/*<pre className="text-xs">{JSON.stringify(userSubjects, null, 1)}</pre>*/}
        {/*<pre className="text-xs">{JSON.stringify(softDeletes, null, 1)}</pre>*/}
        <FormTemplate
          title="What subject areas do you teach?"
          subtitle="Select all that apply to you. This helps us match you to partners and opportunities."
          alert={error && <RequestError request={request} />}
          body={(
            <TagCheckboxCards
              tags={subjects}
              facadeTagNodes={{ data: userSubjects}}
              onChange={handleChange}
              onUpdateDescription={handleDetailsChange}
            />

          )}
        />
      </div>
    </Overlay>
  );
}
